import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Upload, Trash2, Check, Image as ImageIcon } from 'lucide-react';
import GlassButton from '../ui/GlassButton';
import { getEDMImages, deleteEDMImage, EDMImage, apiClient } from '../../services/api';

interface Props {
    open: boolean;
    onClose: () => void;
    onSelect: (url: string) => void;
    projectId?: string;
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const EDMImageBrowser: React.FC<Props> = ({ open, onClose, onSelect, projectId }) => {
    const [images, setImages] = useState<EDMImage[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const loadImages = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getEDMImages();
            setImages(data);
        } catch {
            setImages([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadImages();
            setSelectedUrl(null);
        }
    }, [open, loadImages]);

    const handleUpload = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            for (const file of Array.from(files)) {
                const formData = new FormData();
                formData.append('file', file);
                if (projectId) formData.append('project_id', projectId);
                await apiClient.post('/edm/upload-image', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }
            await loadImages();
        } catch {
            alert('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (filename: string) => {
        if (!window.confirm('Delete this image?')) return;
        try {
            await deleteEDMImage(filename);
            setImages(prev => prev.filter(img => img.filename !== filename));
            if (selectedUrl && images.find(i => i.filename === filename)?.url === selectedUrl) {
                setSelectedUrl(null);
            }
        } catch {
            alert('Delete failed');
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        handleUpload(e.dataTransfer.files);
    };

    const handleInsert = () => {
        if (selectedUrl) {
            onSelect(selectedUrl);
            onClose();
        }
    };

    if (!open) return null;

    return (
        <div className="edm-image-browser-overlay" onClick={onClose}>
            <div className="edm-image-browser" onClick={e => e.stopPropagation()}>
                <div className="edm-image-browser-header">
                    <h2>Image Library</h2>
                    <button className="edm-image-browser-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Upload zone */}
                <div
                    className={`edm-image-upload-zone ${dragOver ? 'drag-over' : ''}`}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Upload size={24} />
                    <span>{uploading ? 'Uploading...' : 'Drop images here or click to upload'}</span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={e => handleUpload(e.target.files)}
                    />
                </div>

                {/* Image grid */}
                <div className="edm-image-grid">
                    {loading ? (
                        <div className="edm-image-browser-empty">Loading...</div>
                    ) : images.length === 0 ? (
                        <div className="edm-image-browser-empty">
                            <ImageIcon size={48} style={{ opacity: 0.3 }} />
                            <p>No images uploaded yet</p>
                        </div>
                    ) : (
                        images.map(img => (
                            <div
                                key={img.filename}
                                className={`edm-image-card ${selectedUrl === img.url ? 'selected' : ''}`}
                                onClick={() => setSelectedUrl(img.url)}
                                onDoubleClick={() => { onSelect(img.url); onClose(); }}
                            >
                                <div className="edm-image-card-thumb">
                                    <img src={img.url} alt={img.filename} loading="lazy" />
                                    {selectedUrl === img.url && (
                                        <div className="edm-image-card-check">
                                            <Check size={20} />
                                        </div>
                                    )}
                                </div>
                                <div className="edm-image-card-info">
                                    <span className="edm-image-card-name" title={img.filename}>
                                        {img.filename}
                                    </span>
                                    <span className="edm-image-card-size">{formatFileSize(img.size)}</span>
                                </div>
                                <button
                                    className="edm-image-card-delete"
                                    onClick={e => { e.stopPropagation(); handleDelete(img.filename); }}
                                    title="Delete"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="edm-image-browser-footer">
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {images.length} image{images.length !== 1 ? 's' : ''}
                        {selectedUrl && ' — double-click or press Insert'}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <GlassButton variant="secondary" onClick={onClose}>Cancel</GlassButton>
                        <GlassButton variant="primary" onClick={handleInsert} disabled={!selectedUrl}>
                            Insert Image
                        </GlassButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EDMImageBrowser;
